/**
 * Manutenção do sistema (P12).
 *
 * É a tela que o proprietário abre quando "não está funcionando". Por isso cada
 * item diz o que está acontecendo E o que fazer — semáforo sem instrução só
 * transfere a angústia.
 *
 * Nenhum botão daqui apaga mensagem, cliente ou serviço. "Reiniciar a conversa"
 * zera o estado do atendimento; o histórico continua no Atendimento.
 */
import { useState } from 'react';
import { useResource } from '../../lib/useResource.js';
import { post, get } from '../../lib/api.js';
import { formatDateTime } from '../../lib/format.js';
import { Alert, Badge, Card, Empty, ErrorBox, Field, Modal, useSubmit, useToast } from '../../components/ui.js';
import { useSession } from '../../lib/session.js';

type Semaforo = 'ok' | 'atencao' | 'parado';

interface Item {
  chave: string;
  titulo: string;
  estado: Semaforo;
  detalhe: string;
  acao?: string;
}

interface Diagnostico {
  verificadoEm: string;
  estadoGeral: Semaforo;
  itens: Item[];
}

const TOM: Record<Semaforo, 'ok' | 'attention' | 'critical'> = {
  ok: 'ok',
  atencao: 'attention',
  parado: 'critical',
};

const ROTULO: Record<Semaforo, string> = {
  ok: 'Tudo certo',
  atencao: 'Atenção',
  parado: 'Parado',
};

export function Sistema() {
  const { can } = useSession();
  const toast = useToast();
  const podeVer = can('system.diagnose');
  const podeAgir = can('system.restart');

  const { data, error, reload, loading } = useResource<Diagnostico>(
    podeVer ? '/api/sistema/diagnostico' : null,
    [],
    // Diagnóstico parado no tempo engana: ele existe justamente para mostrar o
    // que está acontecendo agora.
    { atualizarACada: 15_000 },
  );

  const [acaoAberta, setAcaoAberta] = useState<null | 'whatsapp' | 'travas' | 'fila' | 'ia'>(null);
  const [motivo, setMotivo] = useState('');
  const [qr, setQr] = useState<{ mimeType: string; base64: string } | null>(null);

  const fechar = () => {
    setAcaoAberta(null);
    setMotivo('');
  };

  const reiniciarWhatsApp = useSubmit(async () => {
    const r = await post<{ detalhe: string }>('/api/sistema/whatsapp/reiniciar', { motivo });
    fechar();
    toast(r.detalhe);
    reload();
  });

  const liberar = useSubmit(async () => {
    const r = await post<{ liberadas: number }>('/api/sistema/travas/liberar', { motivo });
    fechar();
    toast(
      r.liberadas === 0
        ? 'Não havia nenhuma conversa travada.'
        : `${r.liberadas} conversa(s) liberada(s).`,
    );
    reload();
  });

  const processarFila = useSubmit(async () => {
    const r = await post<{ entrada: unknown; saida: unknown }>('/api/channel/run-now', {});
    fechar();
    toast(`Fila processada. ${JSON.stringify(r.saida)}`);
    reload();
  });

  const religarIa = useSubmit(async () => {
    const r = await post<{ religado: boolean; detalhe: string }>('/api/sistema/ia/religar', {
      motivo,
    });
    fechar();
    toast(r.detalhe);
    reload();
  });

  const buscarQr = useSubmit(async () => {
    const r = await get<{ qr: { mimeType: string; base64: string } | null; pareada: boolean }>(
      '/api/sistema/whatsapp/qr',
    );
    setQr(r.qr);
    if (r.pareada) toast('O número já está pareado — não há QR para ler.');
  });

  if (!podeVer) {
    return (
      <Card>
        <Empty title="Você não tem permissão para ver o diagnóstico do sistema." />
      </Card>
    );
  }

  return (
    <>
      <ErrorBox error={error} />

      <Card
        title="Diagnóstico"
        description={
          data
            ? `Verificado em ${formatDateTime(data.verificadoEm)}.`
            : 'Lendo o estado de cada parte do sistema.'
        }
        actions={
          <button type="button" onClick={reload} disabled={loading}>
            {loading ? 'Verificando…' : 'Verificar de novo'}
          </button>
        }
      >
        {data ? (
          <>
            <p style={{ marginTop: 0 }}>
              <Badge tone={TOM[data.estadoGeral]}>{ROTULO[data.estadoGeral]}</Badge>
            </p>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Parte</th>
                    <th>Situação</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {data.itens.map((item) => (
                    <tr key={item.chave}>
                      <td style={{ fontWeight: 600 }}>{item.titulo}</td>
                      <td>
                        {item.detalhe}
                        {item.acao ? <div className="hint">{item.acao}</div> : null}
                      </td>
                      <td>
                        <Badge tone={TOM[item.estado]}>{ROTULO[item.estado]}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <Empty title="Carregando…" />
        )}
      </Card>

      <Card
        title="Ações de manutenção"
        description="Nenhuma delas apaga cliente, veículo, serviço, agendamento ou lembrete. Todas ficam registradas na auditoria com o motivo."
      >
        {!podeAgir ? (
          <p className="hint">Só o proprietário e o administrador executam estas ações.</p>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            <Acao
              titulo="Reiniciar o WhatsApp"
              explica="Derruba e sobe a conexão mantendo o número pareado. Use quando parar de receber mensagem. Leva alguns segundos; nada na fila se perde."
              botao="Reiniciar WhatsApp"
              onClick={() => setAcaoAberta('whatsapp')}
            />
            <Acao
              titulo="Religar o assistente"
              explica="Quando o assistente para por falta de crédito ou chave recusada, ele volta sozinho em até 5 minutos. Este botão testa o provedor e religa na hora — se ainda estiver recusando, diz o motivo em vez de fingir que resolveu."
              botao="Testar e religar"
              onClick={() => setAcaoAberta('ia')}
            />
            <Acao
              titulo="Processar a fila agora"
              explica="Força o processamento do que está represado, sem esperar o próximo ciclo."
              botao="Processar fila agora"
              onClick={() => setAcaoAberta('fila')}
            />
            <Acao
              titulo="Liberar conversas travadas"
              explica="A trava evita resposta duplicada e expira sozinha em 3 minutos. Isto é para quando o processo morreu no meio e a espera não faz sentido."
              botao="Liberar travas"
              onClick={() => setAcaoAberta('travas')}
            />
            <ErrorBox error={buscarQr.error} />
            <Acao
              titulo="QR code de pareamento"
              explica="Só existe quando a sessão perdeu o pareamento. Com o número conectado, não aparece nada — e isso é o esperado."
              botao={buscarQr.saving ? 'Buscando…' : 'Ver QR code'}
              onClick={() => void buscarQr.submit()}
            />
            {qr ? (
              <div style={{ textAlign: 'center' }}>
                <img
                  src={`data:${qr.mimeType};base64,${qr.base64}`}
                  alt="QR code para parear o WhatsApp"
                  style={{ maxWidth: 260, imageRendering: 'pixelated' }}
                />
                <p className="hint">WhatsApp › Aparelhos conectados › Conectar um aparelho.</p>
              </div>
            ) : null}
          </div>
        )}
      </Card>

      <Alert tone="info" title="Reiniciar uma conversa específica">
        Fica no Atendimento, dentro da conversa. <strong>Apaga aquele chat</strong> e zera a ficha
        que a IA montou, para você repetir um teste com o mesmo celular e o assistente começar do
        zero. Cliente, veículos, serviços e agendamentos não são tocados.
      </Alert>

      {acaoAberta !== null ? (
      <Modal
        title={
          acaoAberta === 'whatsapp'
            ? 'Reiniciar o WhatsApp'
            : acaoAberta === 'travas'
              ? 'Liberar conversas travadas'
              : acaoAberta === 'ia'
                ? 'Testar e religar o assistente'
                : 'Processar a fila agora'
        }
        onClose={fechar}
      >
        <ErrorBox
          error={reiniciarWhatsApp.error ?? liberar.error ?? processarFila.error ?? religarIa.error}
        />
        <Field label="Motivo" required hint="Fica registrado na auditoria.">
          <input
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ex.: teste do atendimento, parou de receber mensagem"
          />
        </Field>
        <button
          type="button"
          className="button button-primary"
          disabled={motivo.trim().length < 3}
          onClick={() => {
            if (acaoAberta === 'whatsapp') void reiniciarWhatsApp.submit();
            else if (acaoAberta === 'travas') void liberar.submit();
            else if (acaoAberta === 'ia') void religarIa.submit();
            else void processarFila.submit();
          }}
        >
          Confirmar
        </button>
      </Modal>
      ) : null}
    </>
  );
}

function Acao({
  titulo,
  explica,
  botao,
  onClick,
}: {
  titulo: string;
  explica: string;
  botao: string;
  onClick: () => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div>
        <strong>{titulo}</strong>
        <div className="hint">{explica}</div>
      </div>
      <button type="button" onClick={onClick} style={{ whiteSpace: 'nowrap' }}>
        {botao}
      </button>
    </div>
  );
}
